import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiUser,
  FiMail,
  FiCalendar,
  FiClock,
  FiFilm,
  FiTag,
  FiCheck,
  FiX,
  FiPlay,
} from "react-icons/fi";
import { MovieDataList, qc_status, getSections } from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";
import { useRbac } from "../../rbac/RbacContext";
import RejectModal from "./RejectModal";
import ConfirmDialog from "./ConfirmDialog";
import SubmissionPlayer from "./SubmissionPlayer";
import style from "./SubmissionDetail.module.css";
import { qcTone, fmtDate } from "./submissionUtils";

// Like fmtDate but returns null (not "—") for empty values, so hideIfEmpty rows
// collapse instead of showing a dash.
const fmtDateEmpty = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const fmtDuration = (secs) => {
  const n = Number(secs);
  if (!n || Number.isNaN(n)) return null;
  const m = Math.floor(n / 60);
  const s = Math.floor(n % 60);
  if (m >= 60) {
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  }
  return `${m}m ${s}s`;
};


const SubmissionDetail = () => {
  const { metaId } = useParams();
  const navigate = useNavigate();
  const { can } = useRbac();
  const canDecide = can("content", "approve");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [acting, setActing] = useState(null); // "approve" | "reject" | null
  const [rejectOpen, setRejectOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [showTrailer, setShowTrailer] = useState(false);

  const load = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await MovieDataList(metaId);
      // MovieDataList returns the apiResponse envelope: { status, data, message }
      if (res?.status === "error" || !res?.data) {
        setNotFound(true);
        setData(null);
      } else {
        setData(res.data);
      }
    } catch (e) {
      setNotFound(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaId]);

  // The content record stores the Creator's REQUESTED placements directly
  // (requested_placements: [{section_id, category_id}, ...] -- see
  // Publishing.js's Category / Sub Category rows and ContentController).
  // They're only written into SectionCategory.contents once an Admin
  // approves, but we show the requests either way so the reviewer knows
  // what was asked for.
  const [categoryInfoList, setCategoryInfoList] = useState([]);

  useEffect(() => {
    const placements = Array.isArray(data?.requested_placements) ? data.requested_placements : [];
    if (!placements.length) {
      setCategoryInfoList([]);
      return;
    }

    let cancelled = false;
    getSections(1, 500)
      .then((res) => {
        if (cancelled) return;
        const sections = res?.data?.data || [];
        const resolved = placements
          .map((placement) => {
            const section = sections.find((s) => String(s.id) === String(placement.section_id));
            const category = section?.categories?.find((c) => String(c.id) === String(placement.category_id));
            return section && category ? { sectionName: section.name, categoryName: category.name } : null;
          })
          .filter(Boolean);
        setCategoryInfoList(resolved);
      })
      .catch(() => setCategoryInfoList([]));

    return () => {
      cancelled = true;
    };
  }, [data, metaId]);

  const handleApprove = async ({ notify, trustCreator }) => {
    setActing("approve");
    try {
      const res = await qc_status(metaId, {
        status: "Approved",
        notify,
        // Grants "publish without review" to this creator from now on. The
        // backend ignores it on any decision other than Approved.
        trust_creator: !!trustCreator,
      });
      showToast(res?.data?.message || "Submission approved", "success");
      setApproveOpen(false);
      await load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to approve", "error");
    } finally {
      setActing(null);
    }
  };

  const handleReject = async ({ reason, note, notify, removeTrust }) => {
    setActing("reject");
    try {
      const res = await qc_status(metaId, {
        status: "Rejected",
        reason,
        note,
        notify,
        // Takedown only: also put this creator back on the review path, so the
        // pulled item cannot be republished by simply editing it.
        remove_trust: !!removeTrust,
      });
      showToast(
        res?.data?.message ||
          (qc === "Approved"
            ? "Taken down — this content is no longer live"
            : "Submission rejected"),
        "success"
      );
      setRejectOpen(false);
      await load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to reject", "error");
    } finally {
      setActing(null);
    }
  };

  if (loading) {
    return (
      <div className={style.page}>
        <div className={style.centerState}>Loading submission…</div>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className={style.page}>
        <button className={style.backBtn} onClick={() => navigate(-1)}>
          <FiArrowLeft /> Back
        </button>
        <div className={style.centerState}>Submission not found.</div>
      </div>
    );
  }

  const qc = data.qc_status || "Pending";
  const isPending = qc === "Pending";
  const hero = data.landscape || data.portrait || null;
  const duration = fmtDuration(data.duration);
  const trailerLink = data.trailer?.play_link || data.trailer?.external_url;

  const activeStream = showTrailer && trailerLink ? trailerLink : data.HLSPlaylistURL;

  return (
    <div className={style.page}>
      <button className={style.backBtn} onClick={() => navigate(-1)}>
        <FiArrowLeft /> Back to submissions
      </button>

      <div className={style.headerRow}>
        <div>
          <div className={style.titleLine}>
            <h1 className={style.title}>{data.name || "Untitled"}</h1>
            <span className={`${style.badge} ${style[qcTone(qc)]}`}>{qc}</span>
          </div>
          <div className={style.subMeta}>
            {data["content-type"] && <span>{data["content-type"]}</span>}
            {data.creator?.name && (
              <span>
                <FiUser size={13} /> {data.creator.name}
              </span>
            )}
            {data.submission_date && (
              <span>
                <FiCalendar size={13} /> Submitted {fmtDate(data.submission_date)}
              </span>
            )}
            {duration && (
              <span>
                <FiClock size={13} /> {duration}
              </span>
            )}
          </div>
        </div>
      </div>

      {qc === "Rejected" && (data.qc_reason || data.qc_note) && (
        <div className={style.rejectNotice}>
          <div className={style.rejectNoticeTitle}>
            <FiX size={15} /> Rejection details
          </div>
          {data.qc_reason && (
            <div className={style.rejectNoticeRow}>
              <span className={style.rejectNoticeLabel}>Reason:</span>{" "}
              {data.qc_reason}
            </div>
          )}
          {data.qc_note && (
            <div className={style.rejectNoticeRow}>
              <span className={style.rejectNoticeLabel}>Note:</span> {data.qc_note}
            </div>
          )}
        </div>
      )}

      <div className={style.grid}>
        {/* Left column: player + description */}
        <div className={style.mainCol}>
          <div className={style.playerWrap}>
            <SubmissionPlayer src={activeStream} poster={hero} />
          </div>

          {trailerLink && (
            <div className={style.streamToggle}>
              <button
                className={!showTrailer ? style.toggleActive : style.toggleBtn}
                onClick={() => setShowTrailer(false)}
              >
                <FiPlay size={13} /> Main content
              </button>
              <button
                className={showTrailer ? style.toggleActive : style.toggleBtn}
                onClick={() => setShowTrailer(true)}
              >
                <FiFilm size={13} /> Trailer
              </button>
            </div>
          )}

          {categoryInfoList.length > 0 && (
            <section className={style.section}>
              <h3 className={style.sectionTitle}>Categories</h3>
              <div className={style.chips}>
                {categoryInfoList.map((info, index) => (
                  <span className={style.chip} key={`${info.sectionName}-${info.categoryName}-${index}`}>
                    {info.sectionName} &rsaquo; {info.categoryName}
                  </span>
                ))}
              </div>
            </section>
          )}

          {data.description && (
            <section className={style.section}>
              <h3 className={style.sectionTitle}>Description</h3>
              <p className={style.description}>{data.description}</p>
            </section>
          )}

          {Array.isArray(data.casts) && data.casts.length > 0 && (
            <section className={style.section}>
              <h3 className={style.sectionTitle}>Cast</h3>
              <div className={style.people}>
                {data.casts.map((c, i) => (
                  <div className={style.person} key={`cast-${i}`}>
                    <span className={style.personName}>
                      {c.member?.name || c.name || "Unknown"}
                    </span>
                    {c.character_name && (
                      <span className={style.personRole}>as {c.character_name}</span>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {Array.isArray(data.crews) && data.crews.length > 0 && (
            <section className={style.section}>
              <h3 className={style.sectionTitle}>Crew</h3>
              <div className={style.people}>
                {data.crews.map((c, i) => (
                  <div className={style.person} key={`crew-${i}`}>
                    <span className={style.personName}>
                      {c.member?.name || c.name || "Unknown"}
                    </span>
                    {(c.department || c.role) && (
                      <span className={style.personRole}>
                        {c.department || c.role}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Right column: details for the review decision */}
        <aside className={style.sideCol}>
          <div className={style.card}>
            <h3 className={style.cardTitle}>Submission details</h3>
            <dl className={style.detailList}>
              {/* Always-shown core fields */}
              <Detail label="Content type" value={data["content-type"]} icon={<FiFilm size={13} />} />
              <Detail label="Rating" value={data.rating} icon={<FiTag size={13} />} />
              <Detail
                label="Genres"
                value={Array.isArray(data.genres) && data.genres.length ? data.genres.join(", ") : "—"}
              />
              <Detail label="Access" value={data.access} />
              <Detail label="Duration" value={duration} />
              <Detail label="Status" value={data.status} />
              <Detail label="Submitted" value={fmtDate(data.submission_date)} />
              {/* Optional fields — only render when the API actually has a value,
                  so empty submissions don't fill the card with dashes. */}
              <Detail hideIfEmpty label="Original title" value={data.original_title} />
              <Detail hideIfEmpty label="Tagline" value={data.tagline} />
              <Detail hideIfEmpty label="Language" value={data.language} />
              <Detail hideIfEmpty label="Country" value={data.country} />
              <Detail hideIfEmpty label="Studio" value={data.studio} />
              <Detail hideIfEmpty label="Year" value={data.year} />
              <Detail hideIfEmpty label="Release date" value={fmtDateEmpty(data.release_date)} />
              <Detail hideIfEmpty label="Publishing date" value={fmtDateEmpty(data.publishing_date)} />
              <Detail
                hideIfEmpty
                label="IMDb ID"
                value={data.imdb_id}
                render={(v) => (
                  <a
                    className={style.linkValue}
                    href={`https://www.imdb.com/title/${v}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {v}
                  </a>
                )}
              />
              <Detail hideIfEmpty label="Content warning" value={data.content_warning} />
              <Detail
                hideIfEmpty
                label="DRM"
                value={data.drm === true ? "Enabled" : data.drm === false ? "—" : null}
              />
            </dl>
          </div>

          <div className={style.card}>
            <h3 className={style.cardTitle}>Creator</h3>
            <div className={style.creatorRow}>
              <div className={style.creatorAvatar}>
                <FiUser size={18} />
              </div>
              <div>
                <div className={style.creatorName}>
                  {data.creator?.name || "Unknown creator"}
                </div>
                {data.creator?.email && (
                  <a className={style.creatorEmail} href={`mailto:${data.creator.email}`}>
                    <FiMail size={12} /> {data.creator.email}
                  </a>
                )}
              </div>
            </div>
          </div>

          {Array.isArray(data.subtitles) && data.subtitles.length > 0 && (
            <div className={style.card}>
              <h3 className={style.cardTitle}>Subtitles</h3>
              <div className={style.chips}>
                {data.subtitles.map((s, i) => (
                  <span className={style.chip} key={`sub-${i}`}>
                    {s.language || "?"}{s.format ? ` · ${s.format}` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>

      {/* Sticky decision bar — the reviewer approves/rejects where the evidence is.
          Approved content keeps a "Take down" action: a trusted creator's uploads
          publish with no review at all, so there has to be a one-click way to pull
          something that should not be live. */}
      <div className={style.actionBar}>
        <div className={style.actionInfo}>
          {isPending && canDecide ? (
            <span>Review the content above, then make a decision.</span>
          ) : isPending ? (
            <span>You don't have permission to approve or reject submissions.</span>
          ) : (
            <span>
              This submission is <strong className={style[qcTone(qc)]}>{qc}</strong>.
            </span>
          )}
        </div>
        {qc === "Approved" && canDecide && (
          <div className={style.actionBtns}>
            <button
              className={style.rejectBtn}
              disabled={acting === "reject"}
              onClick={() => setRejectOpen(true)}
            >
              <FiX size={16} /> Take down
            </button>
          </div>
        )}
        {isPending && canDecide && (
          <div className={style.actionBtns}>
            <button
              className={style.rejectBtn}
              disabled={acting === "reject"}
              onClick={() => setRejectOpen(true)}
            >
              <FiX size={16} /> Reject
            </button>
            <button
              className={style.approveBtn}
              disabled={acting === "approve"}
              onClick={() => setApproveOpen(true)}
            >
              <FiCheck size={16} /> {acting === "approve" ? "Approving…" : "Approve"}
            </button>
          </div>
        )}
      </div>

      <RejectModal
        open={rejectOpen}
        takedown={qc === "Approved"}
        canRemoveTrust={!!data.creator?.is_trusted_creator}
        creatorName={data.creator?.name || ""}
        title={data.name}
        submitting={acting === "reject"}
        onCancel={() => setRejectOpen(false)}
        onConfirm={handleReject}
      />

      <ConfirmDialog
        open={approveOpen}
        tone="success"
        confirmLabel="Approve"
        title="Approve submission"
        message={`Approve “${data.name}”? Once approved it becomes live-ready.`}
        showNotify
        showTrustCreator={!data.creator?.is_trusted_creator}
        trustCreatorLabel={`Trust ${
          data.creator?.name || "this creator"
        } — publish their future content without review`}
        trustCreatorHelper="Their later uploads and edits go live immediately instead of waiting here. This submission is approved either way."
        submitting={acting === "approve"}
        onCancel={() => setApproveOpen(false)}
        onConfirm={handleApprove}
      />
    </div>
  );
};

// A field is "empty" if it's null/undefined, an empty/whitespace string, or our
// placeholder dash. hideIfEmpty drops the whole row so optional fields that the
// creator never filled in don't clutter the card.
const isEmptyVal = (v) =>
  v === null ||
  v === undefined ||
  (typeof v === "string" && (v.trim() === "" || v.trim() === "—"));

function Detail({ label, value, icon, hideIfEmpty, render }) {
  if (hideIfEmpty && isEmptyVal(value)) return null;
  const shown = value === 0 ? "0" : value || "—";
  return (
    <div className={style.detailItem}>
      <dt className={style.detailLabel}>
        {icon}
        {label}
      </dt>
      <dd className={style.detailValue}>
        {render && !isEmptyVal(value) ? render(value) : shown}
      </dd>
    </div>
  );
}

export default SubmissionDetail;
